import React, { useEffect, useMemo, useRef, useState } from 'react';
import { centeraAuthHeader } from './centeraApiKey';

// Full-screen live truck map shown on TV3 when there aren't enough moves to
// fill that screen (see the measurement in TVView.js) — "we have the full
// screen to work with, so it should just be all map" (Sarah, 2026-09-15).
// Same data source/approach as dispatch's /truck-map page: Leaflet +
// OpenStreetMap tiles (no API key/billing needed), polling the API's
// Samsara-backed active-vehicle endpoint.

const REFRESH_INTERVAL_MS = 30000;
const DEFAULT_CENTER = [41.85, -87.65]; // Chicago — where the fleet operates.
const DEFAULT_ZOOM = 10;

// Floor for the auto-fit zoom (see the flyTo call below) — without this,
// fitting to every active truck can zoom out to a whole-Midwest view
// whenever a couple of them are far from the rest, making individual
// trucks/yards too small to read on the TV.
const MIN_ZOOM = 9;

// How long to wait for Leaflet (loaded via a plain <script> tag in
// index.html, self-hosted — see that file's comment) to be available before
// giving up and showing an error. This is a kiosk device with no one
// watching a console, so a silent failure here is invisible for days — a
// prior version of this component just did nothing forever if window.L
// wasn't set yet, indistinguishable on-screen from every other reason the
// map might not be showing.
const LEAFLET_WAIT_TIMEOUT_MS = 10000;
const LEAFLET_POLL_INTERVAL_MS = 200;

// Rail yards don't move — this is just a visual approximation of a yard's
// footprint (real intermodal yards are roughly this size), not a real
// surveyed boundary.
const YARD_CIRCLE_RADIUS_METERS = 400;

// Vehicle names are like "156 - Don Gavelis" — the company/driver part is
// what's actually identifying on a map; the leading unit number (Sarah,
// 2026-09-15: "just a number" with nothing to go on at a glance) is dropped
// here. Names with no " - " at all (genuinely bare, e.g. Samsara vehicle "0"
// with no assigned driver either) are shown as-is — there's nothing better.
const shortLabel = (name) => {
    const parts = name.split(' - ');
    return parts.length > 1 ? parts.slice(1).join(' - ').trim() : name.trim();
};

// Per Sarah (2026-09-15): grounded = already physically at the yard,
// waiting for a driver (status GROUNDED, keyed by move.from — see the api
// repo's Move Direction convention). Incoming = still inbound by rail, not
// yet grounded (status RECEIVED). Computed from `moves`, passed down from
// TVView.js's own already-live WAMP subscription — no separate polling or
// backend query. (ETA range was dropped per Sarah, 2026-09-15 — "kind of
// sucks now that i'm looking at it.")
const getYardStats = (yard, moves) => {
    let groundedCount = 0;
    let incomingCount = 0;

    Object.values(moves).forEach((move) => {
        if (move.from !== yard.nickname) {
            return;
        }

        if (move.status === 'GROUNDED') {
            groundedCount++;
        } else if (move.status === 'RECEIVED') {
            incomingCount++;
        }
    });

    return { groundedCount, incomingCount };
};

const formatYardStats = (stats) => `${stats.groundedCount} grounded, ${stats.incomingCount} incoming`;

const buildYardTooltip = (yard, moves) =>
    `<strong>${yard.nickname}</strong><br />${formatYardStats(getYardStats(yard, moves))}`;

const yardBoxStyle = {
    position: 'fixed',
    top: 16,
    left: 16,
    zIndex: 10000,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    color: '#fff',
    borderRadius: 10,
    padding: '16px 22px',
    fontFamily: 'sans-serif',
    fontSize: 30,
    fontWeight: 600,
    lineHeight: 1.5,
    maxHeight: 'calc(100vh - 32px)',
    overflowY: 'auto',
};

const errorOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 24,
    fontFamily: 'sans-serif',
    textAlign: 'center',
    padding: 40,
};

const TruckMapOverlay = ({ moves }) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef(new Map());
    const yardCirclesRef = useRef(new Map());
    const yardsRef = useRef([]);
    const [isMapReady, setIsMapReady] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [yards, setYards] = useState([]);

    useEffect(() => {
        let cancelled = false;
        let waited = 0;

        const tryInit = () => {
            if (cancelled) {
                return;
            }

            if (!window.L) {
                waited += LEAFLET_POLL_INTERVAL_MS;
                if (waited >= LEAFLET_WAIT_TIMEOUT_MS) {
                    setLoadError('Leaflet script never loaded (check network access to this server)');
                    return;
                }
                setTimeout(tryInit, LEAFLET_POLL_INTERVAL_MS);
                return;
            }

            if (!mapContainerRef.current) {
                setTimeout(tryInit, LEAFLET_POLL_INTERVAL_MS);
                return;
            }

            try {
                const L = window.L;
                const map = L.map(mapContainerRef.current).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '&copy; OpenStreetMap contributors',
                    maxZoom: 19,
                }).addTo(map);

                mapRef.current = map;
                setIsMapReady(true);

                // The map is created before its container has real layout dimensions
                // in some TV-boot timing cases — nudge Leaflet to recompute size.
                setTimeout(() => map.invalidateSize(), 200);
            } catch (err) {
                setLoadError(`Failed to create map: ${err.message}`);
            }
        };

        tryInit();

        return () => {
            cancelled = true;
            // This overlay mounts/unmounts every time TV3 toggles between the
            // map and the real move board (as move counts cross the
            // screen-3 threshold) — potentially many times over the months
            // this Pi runs unattended between reboots. Leaflet registers its
            // own window-level listeners (resize, etc.) that React removing
            // the container div from the DOM does NOT clean up on its own;
            // without an explicit map.remove() those leak a little more on
            // every toggle, which compounds over that kind of timescale.
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, []);

    // Draw rail yards as geofence-style circles, once — yards themselves
    // don't move, so unlike vehicle positions this never needs to re-fetch.
    useEffect(() => {
        if (!isMapReady) {
            return;
        }

        const L = window.L;
        const map = mapRef.current;
        let cancelled = false;

        fetch('https://api.centeratransport.com/truck-map/yards', { headers: centeraAuthHeader })
            .then((res) => res.json())
            .then((data) => {
                if (cancelled || data.status !== 'ok') {
                    return;
                }

                yardsRef.current = data.message;
                setYards(data.message);

                data.message.forEach((yard) => {
                    const circle = L.circle([yard.lat, yard.lng], {
                        radius: YARD_CIRCLE_RADIUS_METERS,
                        color: '#546e7a',
                        weight: 1,
                        fillColor: '#546e7a',
                        fillOpacity: 0.15,
                        interactive: false,
                    });
                    circle.addTo(map);
                    circle.bindTooltip(buildYardTooltip(yard, moves), {
                        permanent: true,
                        direction: 'center',
                        className: 'yard-label',
                    });
                    yardCirclesRef.current.set(yard.nickname, circle);
                });
            })
            .catch((err) => {
                console.error('TruckMapOverlay: failed to load rail yards', err);
            });

        return () => {
            cancelled = true;
        };
        // Intentionally omits `moves` — only used for the circles' initial
        // tooltip text at fetch time; the effect below keeps them current
        // from then on.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMapReady]);

    // Keep yard tooltips (grounded/incoming counts) current as moves update
    // live over WAMP — same moves data TVView.js already renders the board
    // from, just recomputed per yard; no separate polling/backend query.
    useEffect(() => {
        yardCirclesRef.current.forEach((circle, nickname) => {
            const yard = yardsRef.current.find((y) => y.nickname === nickname);
            if (yard) {
                circle.setTooltipContent(buildYardTooltip(yard, moves));
            }
        });
    }, [moves]);

    // Same per-yard numbers as the map circles, listed in a readable box
    // instead of squinting at small text scattered across the map.
    const yardStatsList = useMemo(() => {
        return [...yards]
            .sort((a, b) => a.nickname.localeCompare(b.nickname))
            .map((yard) => ({ yard, stats: getYardStats(yard, moves) }));
    }, [yards, moves]);

    useEffect(() => {
        if (!isMapReady) {
            return;
        }

        const L = window.L;
        const map = mapRef.current;
        let cancelled = false;

        const refresh = () => {
            fetch('https://api.centeratransport.com/truck-map/vehicles', { headers: centeraAuthHeader })
                .then((res) => res.json())
                .then((data) => {
                    if (cancelled || data.status !== 'ok') {
                        return;
                    }

                    const seenIds = new Set();

                    data.message.forEach((vehicle) => {
                        seenIds.add(vehicle.id);
                        const position = [vehicle.lat, vehicle.lng];
                        const existingMarker = markersRef.current.get(vehicle.id);

                        if (existingMarker) {
                            existingMarker.setLatLng(position);
                            return;
                        }

                        const marker = L.marker(position).addTo(map);
                        marker.bindTooltip(shortLabel(vehicle.name), {
                            permanent: true,
                            direction: 'right',
                            offset: [14, 0],
                            className: 'truck-label-tv',
                        });
                        markersRef.current.set(vehicle.id, marker);
                    });

                    markersRef.current.forEach((marker, id) => {
                        if (!seenIds.has(id)) {
                            map.removeLayer(marker);
                            markersRef.current.delete(id);
                        }
                    });

                    // Smoothly zoom/pan to fit the actual spread of trucks,
                    // every refresh — per Sarah (2026-09-15), the view should
                    // track the fleet as it changes, not just fit once. Most
                    // refreshes this is a barely-visible nudge since truck
                    // positions don't jump far in 30s.
                    //
                    // Fitting to *all* active trucks can pull the zoom way
                    // out when they're scattered across a wide area (e.g.
                    // out toward Michigan/Wisconsin) — floor the zoom so it
                    // never goes wider than a regional view, even if that
                    // means some outlier trucks fall outside the frame.
                    if (data.message.length > 0) {
                        const bounds = L.latLngBounds(data.message.map((v) => [v.lat, v.lng]));
                        const fitZoom = map.getBoundsZoom(bounds, false, [40, 40]);
                        const targetZoom = Math.max(fitZoom, MIN_ZOOM);
                        map.flyTo(bounds.getCenter(), targetZoom, {duration: 1.5});
                    }
                })
                .catch((err) => {
                    console.error('TruckMapOverlay: failed to load vehicle locations', err);
                });
        };

        refresh();
        const intervalId = setInterval(refresh, REFRESH_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [isMapReady]);

    if (loadError) {
        return (
            <div data-truck-map-overlay="true" style={{...errorOverlayStyle, backgroundColor: '#000'}}>
                Truck map failed to load:<br />{loadError}
            </div>
        );
    }

    return (
        <>
            <div
                ref={mapContainerRef}
                data-truck-map-overlay="true"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    zIndex: 9999,
                }}
            />
            {yardStatsList.length > 0 && (
                <div data-truck-map-overlay="true" style={yardBoxStyle}>
                    {yardStatsList.map(({ yard, stats }) => (
                        <div key={yard.nickname} style={{whiteSpace: 'nowrap'}}>
                            <strong>{yard.nickname}</strong>: {formatYardStats(stats)}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default TruckMapOverlay;
